import Box from '@mui/material/Box';
import { type ReactNode, useMemo, useState } from 'react';
import {
  type AddedCompetitionFilter,
  useCompetitionFilterSearch,
} from '../../lib/competition-filter-search';
import {
  availableCompetitionKeys,
  type CompetitionDateWindow,
  type CompetitionPanelDimensions,
  type CompetitionPanelValues,
  type CompetitionSelectedFilters,
  useCompetitionCreativeIds,
  useCompetitionPanelValues,
} from '../../lib/competitionFilters.hooks';
import { dateWindowFromRange } from '../../lib/dateUtils';
import { CompetitionAddFilterMenu } from './CompetitionAddFilterMenu';
import {
  COMPETITION_FAMILIES,
  COMPETITION_FILTER_INDEX,
  COMPETITION_FILTER_KEYS,
  COMPETITION_NARROWING_KEYS,
  COMPETITION_SCOPE_KEYS,
  type CompetitionFilterKey,
} from './competitionFilterCatalog';
import { FilterControl } from './FilterControl';
import { withJustAdded } from './justAdded';
import { FILTER_BAR_ROW_SX } from './menuStyles';
import { ScopeLabel } from './ScopeLabel';
import { MultiScopeSelectControl, ScopeRow } from './ScopeRow';
import type { ScopeOption, ScopeState } from './types';
import { useScopeAccounts } from './useScopeSelection';

const SCOPE_HINT =
  'The competitor data every panel reads. Competitor coverage is per product, so a filter with no data in this scope is not offered.';

interface CompetitionFilterBarProps {
  scope: ScopeState;
  onScopeChange: (scope: ScopeState) => void;
}

export function narrowingSelection(
  added: AddedCompetitionFilter[],
): Partial<Record<CompetitionFilterKey, string[]>> {
  return Object.fromEntries(
    added
      .filter((filter) => COMPETITION_NARROWING_KEYS.has(filter.key) && filter.values.length > 0)
      .map((filter) => [filter.key, filter.values]),
  );
}

/** Every active filter, applied to the data itself — unlike `narrowingSelection`, which is scoped
 * to just the subset that should also cascade into narrowing *other* filters' pickable values
 * (Duration and Creative ID deliberately don't cascade, but still have to filter the Creatives/Top
 * Metrics query, or picking one would silently do nothing). */
export function appliedSelection(
  added: AddedCompetitionFilter[],
): Record<string, string[] | undefined> {
  return Object.fromEntries(
    added.filter((filter) => filter.values.length > 0).map((filter) => [filter.key, filter.values]),
  );
}

function CreativeIdFilterControl({
  filter,
  dateWindow,
  accountIds,
  autoOpen,
  onChange,
  onRemove,
}: {
  filter: AddedCompetitionFilter;
  dateWindow: CompetitionDateWindow;
  accountIds: number[];
  autoOpen: boolean;
  onChange: (values: string[]) => void;
  onRemove: () => void;
}): ReactNode {
  const { data, isFetching, onSearchChange } = useCompetitionCreativeIds(dateWindow, accountIds);
  const bucket = data?.creativeId;

  return (
    <FilterControl
      label={COMPETITION_FILTER_INDEX.creativeId.label}
      tone={COMPETITION_FAMILIES[COMPETITION_FILTER_INDEX.creativeId.family].tone}
      options={bucket?.values ?? []}
      values={filter.values}
      onChange={onChange}
      onRemove={onRemove}
      onSearchChange={onSearchChange}
      autoOpen={autoOpen}
      isLoading={isFetching}
      truncated={bucket?.truncated ?? false}
    />
  );
}

function CompetitionFilterBarBody({
  scope,
  applyScope,
  accountIds,
}: {
  scope: ScopeState;
  applyScope: (scope: ScopeState) => void;
  accountIds: number[];
}): ReactNode {
  const [justAdded, setJustAdded] = useState<CompetitionFilterKey | null>(null);
  const { added, addFilter, removeFilter, patchFilter } = useCompetitionFilterSearch();

  const dateWindow = dateWindowFromRange(scope.range, new Date());
  const activeKeys = new Set(added.map((filter) => filter.key));

  const selected = useMemo<CompetitionSelectedFilters>(() => narrowingSelection(added), [added]);

  const { data: panelValues, isPending: panelValuesPending } = useCompetitionPanelValues(
    COMPETITION_FILTER_KEYS as CompetitionPanelDimensions,
    selected,
    dateWindow,
    accountIds,
  );

  const availableKeys = availableCompetitionKeys(panelValues, activeKeys);

  const onAddFilter = (key: CompetitionFilterKey): void => {
    setJustAdded(key);
    addFilter(key);
  };

  const scopeValues = (key: CompetitionFilterKey): string[] =>
    added.find((filter) => filter.key === key)?.values ?? [];

  const scopeOptions = (key: CompetitionFilterKey): ScopeOption[] =>
    (panelValues?.[key as keyof CompetitionPanelValues]?.values ?? []).map((value) => ({
      id: value,
      label: value,
    }));

  return (
    <Box sx={FILTER_BAR_ROW_SX}>
      <ScopeLabel title={SCOPE_HINT} />
      <ScopeRow
        scope={scope}
        onChange={applyScope}
        extraControls={COMPETITION_SCOPE_KEYS.filter((key) => availableKeys.has(key)).map((key) => (
          <MultiScopeSelectControl
            key={key}
            label={COMPETITION_FILTER_INDEX[key].label}
            placeholderLabel={`All ${COMPETITION_FILTER_INDEX[key].label.toLowerCase()}s`}
            options={scopeOptions(key)}
            selected={scopeValues(key)}
            onChange={(values) => patchFilter(key, values)}
          />
        ))}
      />

      {withJustAdded(added, justAdded)
        .filter((filter) => !COMPETITION_SCOPE_KEYS.includes(filter.key))
        .map((filter) => {
          const definition = COMPETITION_FILTER_INDEX[filter.key];
          const onChange = (values: string[]): void => patchFilter(filter.key, values);
          const onRemove = (): void => {
            if (justAdded === filter.key) setJustAdded(null);
            removeFilter(filter.key);
          };
          const autoOpen = justAdded === filter.key;

          if (definition.kind === 'name') {
            return (
              <CreativeIdFilterControl
                key={filter.key}
                filter={filter}
                dateWindow={dateWindow}
                accountIds={accountIds}
                onChange={onChange}
                onRemove={onRemove}
                autoOpen={autoOpen}
              />
            );
          }

          const bucket = panelValues?.[filter.key as keyof CompetitionPanelValues];

          return (
            <FilterControl
              key={filter.key}
              label={definition.label}
              tone={COMPETITION_FAMILIES[definition.family].tone}
              options={bucket?.values ?? []}
              {...(bucket?.labels ? { valueLabels: bucket.labels } : {})}
              values={filter.values}
              onChange={onChange}
              onRemove={onRemove}
              autoOpen={autoOpen}
              isLoading={definition.kind === 'panel' && panelValuesPending}
              truncated={bucket?.truncated ?? false}
            />
          );
        })}

      <CompetitionAddFilterMenu
        activeKeys={activeKeys}
        availableKeys={availableKeys}
        onAddFilter={onAddFilter}
      />
    </Box>
  );
}

export function CompetitionFilterBar({
  scope,
  onScopeChange,
}: CompetitionFilterBarProps): ReactNode {
  const { accountIds } = useScopeAccounts(scope);
  return (
    <CompetitionFilterBarBody scope={scope} applyScope={onScopeChange} accountIds={accountIds} />
  );
}
